import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMoneyIn } from '../utils/api';

/* Money In — every payment that came in on a day (or range), whatever the
   project's status. Cash marked Collected + financing marked Funded, by the
   date entered on the payment. Financing is shown net of the dealer fee.   */

const TZ = 'America/Los_Angeles';
const SERVICE_LABELS = { Roof: 'Roofing', EC: 'Exterior Coating', Win: 'Windows', Gut: 'Gutters' };

const todayCA = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
// date arithmetic in UTC so the day never slips across time zones
const addDays = (iso, n) => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
};
const prettyDay = iso => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};
const shortDay = iso => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
};
const money = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CSS = `
.mi-bar{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:18px}
.mi-arrow{width:38px;height:38px;border-radius:10px;border:1px solid #d6dfdb;background:#fff;font-size:18px;cursor:pointer;color:#0D3B2E}
.mi-arrow:hover{background:#EEF3F0}
.mi-day{font-size:17px;font-weight:700;color:#0D3B2E;min-width:260px;text-align:center}
.mi-seg{display:inline-flex;border:1px solid #d6dfdb;border-radius:10px;overflow:hidden;margin-left:auto}
.mi-seg button{border:none;background:#fff;padding:8px 14px;font-weight:700;font-size:13px;cursor:pointer;color:#4A5D55}
.mi-seg button.on{background:#0D3B2E;color:#fff}
.mi-tiles{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:20px}
@media(max-width:900px){.mi-tiles{grid-template-columns:repeat(2,1fr)}}
.mi-tile{background:#fff;border:1px solid #E3E9E5;border-radius:14px;padding:16px 18px}
.mi-tile.main{background:linear-gradient(135deg,#0D3B2E,#1B5E40);border:none}
.mi-tile .k{font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#7a8a82}
.mi-tile .v{font-size:24px;font-weight:800;color:#0D3B2E;margin-top:6px}
.mi-tile .s{font-size:12px;color:#7a8a82;margin-top:4px}
.mi-tile.main .k,.mi-tile.main .s{color:rgba(255,255,255,.75)}
.mi-tile.main .v{color:#F0C842;font-size:28px}
.mi-tile.fee .v{color:#B23B2E}
.mi-sec{margin-bottom:20px}
.mi-sec table{width:100%;border-collapse:collapse}
.mi-sec th{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#7a8a82;text-align:left;padding:10px 12px;border-bottom:1px solid #E3E9E5;background:#F8FAF9}
.mi-sec td{padding:10px 12px;border-bottom:1px solid #EEF2F0;font-size:14px}
.mi-sec tr.click{cursor:pointer}
.mi-sec tr.click:hover td{background:#F5F9F7}
.mi-sec td.r,.mi-sec th.r{text-align:right;white-space:nowrap}
.mi-sec tfoot td{font-weight:800;background:#F8FAF9}
.mi-tag{display:inline-block;font-size:11px;font-weight:700;padding:2px 8px;border-radius:999px;background:#EEF3F0;color:#4A5D55;margin-left:6px}
.mi-tag.warn{background:#FFF3E0;color:#B35C00}
.mi-tag.closed{background:#ECEFF1;color:#546E7A}
.mi-empty{padding:18px;color:#7a8a82;font-size:14px}
.mi-sec .table-wrap{overflow-x:auto}
@media(max-width:600px){.mi-day{min-width:0;flex:1;font-size:15px}.mi-seg{margin-left:0}.mi-tile .v{font-size:20px}.mi-tile.main .v{font-size:22px}.mi-sec td,.mi-sec th{padding:8px}}
.mi-note{background:#FFF8E6;border:1px solid #F3D27A;border-radius:12px;padding:12px 16px;font-size:13px;color:#6B5200;margin-bottom:20px}
.mi-fee{color:#B23B2E}
.mi-net{font-weight:800;color:#0D3B2E}
`;

function JobCell({ r }) {
  const closed = /closed|complete|cancel/i.test(`${r.sales_status || ''} ${r.prod_status || ''}`);
  return (
    <>
      <div style={{ fontWeight: 700 }}>{r.customer_name || '—'}</div>
      <div style={{ fontSize: 12, color: '#7a8a82' }}>
        {r.po_number || 'no PO'}{r.city ? ` · ${r.city}` : ''}
        {r.sales_status && <span className={'mi-tag' + (closed ? ' closed' : '')}>{r.sales_status}</span>}
      </div>
    </>
  );
}

export default function MoneyIn({ embedded = false }) {   // embedded = shown as the Collection report's "Collected" tab
  const navigate = useNavigate();
  const [mode, setMode] = useState('day');           // 'day' | 'range'
  const [day, setDay] = useState(todayCA());
  const [from, setFrom] = useState(addDays(todayCA(), -6));
  const [to, setTo] = useState(todayCA());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const qFrom = mode === 'day' ? day : from;
  const qTo = mode === 'day' ? day : to;

  const load = useCallback(async () => {
    if (!qFrom || !qTo || qFrom > qTo) return;
    setLoading(true); setError('');
    try { setData(await getMoneyIn(qFrom, qTo)); }
    catch (e) { setError(e.response?.data?.error || 'Could not load the report'); setData(null); }
    finally { setLoading(false); }
  }, [qFrom, qTo]);
  useEffect(() => { load(); }, [load]);

  const open = r => r.po_number && navigate(`/update?po=${encodeURIComponent(r.po_number)}`);
  const t = data?.totals;
  const isRange = mode === 'range';
  const isToday = day === todayCA();

  return (
    <>
      <style>{CSS}</style>
      {!embedded && (
        <div className="page-header">
          <h2>Money In</h2>
          <div style={{ fontSize: 13, color: '#888' }}>Every payment received — cash collected and financing funded — by the date it came in. California time.</div>
        </div>
      )}
      <div className={embedded ? '' : 'page-body'}>
        <div className="mi-bar">
          {!isRange ? (
            <>
              <button className="mi-arrow" onClick={() => setDay(d => addDays(d, -1))} aria-label="Previous day">‹</button>
              <div className="mi-day">{prettyDay(day)}{isToday && <span className="mi-tag">Today</span>}</div>
              <button className="mi-arrow" onClick={() => setDay(d => addDays(d, 1))} aria-label="Next day">›</button>
              <input className="form-input" type="date" value={day} onChange={e => e.target.value && setDay(e.target.value)} style={{ width: 170 }} />
              {!isToday && <button className="btn btn-sm" onClick={() => setDay(todayCA())}>Today</button>}
            </>
          ) : (
            <>
              <label className="form-label" style={{ margin: 0 }}>From</label>
              <input className="form-input" type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ width: 170 }} />
              <label className="form-label" style={{ margin: 0 }}>To</label>
              <input className="form-input" type="date" value={to} onChange={e => setTo(e.target.value)} style={{ width: 170 }} />
              {from > to && <span style={{ color: '#c62828', fontSize: 13 }}>"From" is after "To"</span>}
            </>
          )}
          <div className="mi-seg">
            <button className={!isRange ? 'on' : ''} onClick={() => setMode('day')}>One day</button>
            <button className={isRange ? 'on' : ''} onClick={() => setMode('range')}>Date range</button>
          </div>
        </div>

        {qTo > todayCA() && (
          <div className="mi-note"><b>{qFrom > todayCA() ? 'This day hasn\u2019t happened yet.' : 'Part of this range is in the future.'}</b> Money can only come in on days that have passed, so payments dated in the future are listed under "not counted" below. If one was really collected, open the record and fix its date.</div>
        )}
        {error && <div className="mi-note" style={{ background: '#FDECEA', borderColor: '#F5B7B1', color: '#8E1B10' }}>{error}</div>}
        {loading && !data && <div className="loader"><div className="spinner" /></div>}

        {data && (
          <>
            <div className="mi-tiles">
              <div className="mi-tile main">
                <div className="k">Money in</div>
                <div className="v">{money(t.moneyIn)}</div>
                <div className="s">Cash + financing, after dealer fees</div>
              </div>
              <div className="mi-tile">
                <div className="k">Cash collected</div>
                <div className="v">{money(t.cash)}</div>
                <div className="s">{data.cash.length} payment{data.cash.length === 1 ? '' : 's'}</div>
              </div>
              <div className="mi-tile">
                <div className="k">Financing funded</div>
                <div className="v">{money(t.financingNet)}</div>
                <div className="s">{data.financing.length} loan{data.financing.length === 1 ? '' : 's'} · {money(t.financed)} before dealer fees</div>
              </div>
              <div className="mi-tile fee">
                <div className="k">Dealer fees</div>
                <div className="v">− {money(t.dealerFees)}</div>
                <div className="s">Kept by the lenders</div>
              </div>
            </div>

            {data.missingFundedDate.length > 0 && (
              <div className="mi-note">
                <b>{data.missingFundedDate.length} loan{data.missingFundedDate.length === 1 ? ' is' : 's are'} marked Funded but {data.missingFundedDate.length === 1 ? 'has' : 'have'} no funded date</b>, so {data.missingFundedDate.length === 1 ? 'it does' : 'they do'} not appear on any day:{' '}
                {data.missingFundedDate.map((r, i) => (
                  <span key={i}>
                    {i > 0 && ', '}
                    <a href={`/update?po=${encodeURIComponent(r.po_number || '')}`} onClick={e => { e.preventDefault(); open(r); }} style={{ color: '#6B5200', fontWeight: 700 }}>
                      {r.customer_name || r.po_number} ({money(r.amount)})
                    </a>
                  </span>
                ))}
              </div>
            )}

            {isRange && data.byDay.length > 1 && (
              <div className="card mi-sec">
                <div className="card-header"><h3>Day by day</h3></div>
                <div className="table-wrap" style={{ border: 'none' }}>
                  <table>
                    <thead><tr><th>Day</th><th className="r">Cash</th><th className="r">Loan amount</th><th className="r">Dealer fees</th><th className="r">Money in</th></tr></thead>
                    <tbody>
                      {data.byDay.map(d => (
                        <tr key={d.date} className="click" onClick={() => { setDay(d.date); setMode('day'); }}>
                          <td>{prettyDay(d.date)}</td>
                          <td className="r">{money(d.cash)}</td>
                          <td className="r">{money(d.financed)}</td>
                          <td className="r mi-fee">{d.fees ? '− ' + money(d.fees) : '—'}</td>
                          <td className="r mi-net">{money(d.moneyIn)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="card mi-sec">
              <div className="card-header"><h3>Cash collected</h3></div>
              {data.cash.length === 0 ? <div className="mi-empty">No cash payments {isRange ? 'in this range' : 'on this day'}.</div> : (
                <div className="table-wrap" style={{ border: 'none' }}>
                  <table>
                    <thead><tr>{isRange && <th>Date</th>}<th>Customer</th><th>Service</th><th>Payment</th><th>Method</th><th className="r">Amount</th></tr></thead>
                    <tbody>
                      {data.cash.map((r, i) => (
                        <tr key={i} className="click" onClick={() => open(r)} title="Open this record">
                          {isRange && <td>{shortDay(r.date)}</td>}
                          <td><JobCell r={r} /></td>
                          <td>{SERVICE_LABELS[r.service] || r.service}</td>
                          <td>{r.payment_type}{r.statusMissing && <span className="mi-tag warn">status not set</span>}</td>
                          <td>{r.pay_method || '—'}{r.check_num ? ` #${r.check_num}` : ''}</td>
                          <td className="r mi-net">{money(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr><td colSpan={isRange ? 5 : 4}>Total cash</td><td className="r">{money(t.cash)}</td></tr></tfoot>
                  </table>
                </div>
              )}
            </div>

            <div className="card mi-sec">
              <div className="card-header"><h3>Financing funded</h3></div>
              {data.financing.length === 0 ? <div className="mi-empty">No financing funded {isRange ? 'in this range' : 'on this day'}.</div> : (
                <div className="table-wrap" style={{ border: 'none' }}>
                  <table>
                    <thead><tr>{isRange && <th>Date</th>}<th>Customer</th><th>Service</th><th>Lender</th><th className="r">Loan amount</th><th className="r">Dealer fee</th><th className="r">Funded (we receive)</th></tr></thead>
                    <tbody>
                      {data.financing.map((r, i) => (
                        <tr key={i} className="click" onClick={() => open(r)} title="Open this record">
                          {isRange && <td>{shortDay(r.date)}</td>}
                          <td><JobCell r={r} /></td>
                          <td>{SERVICE_LABELS[r.service] || r.service}</td>
                          <td>{r.lender_name}{r.statusMissing && <span className="mi-tag warn">status not set</span>}</td>
                          <td className="r">{money(r.amount)}</td>
                          <td className="r mi-fee">{r.feeMissing ? <span className="mi-tag warn">not entered</span> : (r.dealer_fee ? '− ' + money(r.dealer_fee) : '$0.00')}</td>
                          <td className="r mi-net">{money(r.net)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot><tr><td colSpan={isRange ? 4 : 3}>Total financing</td><td className="r">{money(t.financed)}</td><td className="r mi-fee">− {money(t.dealerFees)}</td><td className="r">{money(t.financingNet)}</td></tr></tfoot>
                  </table>
                </div>
              )}
            </div>

            {data.notCounted.length > 0 && (
              <div className="card mi-sec">
                <div className="card-header"><h3>Dated {isRange ? 'in this range' : 'this day'} but not counted</h3></div>
                <div style={{ padding: '0 16px 6px', fontSize: 13, color: '#7a8a82' }}>These have a date that matches but their status says the money has not come in. If it has, open the record and fix the status.</div>
                <div className="table-wrap" style={{ border: 'none' }}>
                  <table>
                    <thead><tr>{isRange && <th>Date</th>}<th>Customer</th><th>What</th><th>Why not counted</th><th className="r">Amount</th></tr></thead>
                    <tbody>
                      {data.notCounted.map((r, i) => (
                        <tr key={i} className="click" onClick={() => open(r)} title="Open this record">
                          {isRange && <td>{shortDay(r.date)}</td>}
                          <td><JobCell r={r} /></td>
                          <td>{SERVICE_LABELS[r.service] || r.service} · {r.kind === 'cash' ? r.payment_type : `Loan — ${r.lender_name}`}</td>
                          <td><span className="mi-tag warn">{r.reason}</span></td>
                          <td className="r">{money(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
